import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from 'contexts/AuthContext';
import { supabase } from 'config/supabase';
import { Subscription } from 'types';

interface SubscriptionGuardProps {
  children: React.ReactNode;
}

/**
 * SubscriptionGuard Component
 *
 * Guards the application to ensure users have an active subscription.
 * Redirects to /paywall if no active subscription is found.
 *
 * Exempted routes:
 * - /paywall (the subscription page itself)
 * - /payment-status (payment verification page, webhook may be pending)
 * - /login, /signup (public routes, handled by ProtectedRoute)
 *
 * NOTE: /onboarding is NOT exempted - requires subscription (enforced by ProtectedRoute)
 */
export const SubscriptionGuard: React.FC<SubscriptionGuardProps> = ({ children }) => {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [hasSubscription, setHasSubscription] = useState<boolean | null>(null);
  const [checking, setChecking] = useState(true);

  // Routes that don't require subscription check
  const exemptedRoutes = ['/paywall', '/payment-status', '/login', '/signup'];
  const isExemptedRoute = exemptedRoutes.some((route) => location.pathname.startsWith(route));

  useEffect(() => {
    const checkSubscription = async () => {
      // Skip check if user is not logged in or on exempted route
      if (!user || isExemptedRoute) {
        setChecking(false);
        return;
      }

      try {
        // Use Postgres function to get the most relevant subscription
        // (active subscription OR canceled subscription within period)
        const { data, error } = await supabase
          .rpc('get_active_subscription', { p_user_id: user.id })
          .returns<Subscription>()
          .single();

        if (error || !data) {
          // No subscription found
          setHasSubscription(false);
          setChecking(false);
          return;
        }

        // If get_active_subscription returns a row, user has valid access
        // (function already handles active OR canceled-within-period logic)
        setHasSubscription(true);
      } catch (err) {
        console.error('Error checking subscription:', err);
        setHasSubscription(false);
      } finally {
        setChecking(false);
      }
    };

    checkSubscription();
  }, [user, location.pathname, isExemptedRoute]);

  // Redirect to paywall if no subscription
  useEffect(() => {
    if (!checking && !authLoading && user && !isExemptedRoute && hasSubscription === false) {
      console.log('[SubscriptionGuard] No active subscription, redirecting to /paywall');
      navigate('/paywall', { replace: true });
    }
  }, [checking, authLoading, user, isExemptedRoute, hasSubscription, navigate]);

  // Show loading state while checking
  if (authLoading || (checking && !isExemptedRoute)) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-gray-600">Checking subscription...</div>
      </div>
    );
  }

  return <>{children}</>;
};
